<template>
  <section
    ref="sectionRef"
    class="how-we-work"
    :class="{ 'how-we-work--visible': isVisible }"
    @mousemove="handleMouseMove"
    @mouseleave="resetImagePosition"
  >
    <div class="content">
      <span class="content-line"></span>

      <h2 class="title">
        Как мы работаем<span class="question">?</span>
      </h2>

      <p class="description">
        Мы анализируем резюме кандидата, который откликнулся на вакансию,
        и формируем для работодателя или скаута подробную аннотацию о нём.
      </p>

      <p class="description description--secondary">
        В ней мы показываем, насколько кандидат соответствует требованиям,
        какие у него есть сильные стороны, на что стоит обратить внимание
        и какие возможны риски при найме.
      </p>

      <p class="description description--secondary">
        Это помогает работодателям быстрее принимать решения
        и экономит время на ручном анализе откликов.
      </p>
    </div>

    <div class="image-wrapper">
      <img
        :src="currentImage"
        alt="Как мы работаем"
        class="image"
        :style="imageStyle"
      />
    </div>
  </section>
</template>

<script setup>
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
} from "vue";

const lightImage = new URL(
  "../assets/how-we-work-light.webp",
  import.meta.url
).href;

const darkImage = new URL(
  "../assets/how-we-work-dark.webp",
  import.meta.url
).href;

const sectionRef = ref(null);
const isVisible = ref(false);

const theme = ref(
  document.documentElement.getAttribute("data-theme") ||
    "light"
);

const translateX = ref(0);
const translateY = ref(0);
const rotateX = ref(0);
const rotateY = ref(0);

const currentImage = computed(() => {
  return theme.value === "dark"
    ? darkImage
    : lightImage;
});

const imageStyle = computed(() => ({
  transform: `
    perspective(1400px)
    translate3d(
      ${translateX.value}px,
      ${translateY.value}px,
      0
    )
    rotateX(${rotateX.value}deg)
    rotateY(${rotateY.value}deg)
    scale(1.005)
  `,
}));

let themeObserver;
let visibilityObserver;

const handleMouseMove = (event) => {
  if (window.innerWidth <= 768) {
    return;
  }

  const section = sectionRef.value;

  if (!section) {
    return;
  }

  const rect = section.getBoundingClientRect();

  const relativeX =
    (event.clientX - rect.left) / rect.width;

  const relativeY =
    (event.clientY - rect.top) / rect.height;

  translateX.value =
    (relativeX - 0.5) * 6;

  translateY.value =
    (relativeY - 0.5) * 5;

  rotateY.value =
    (relativeX - 0.5) * 1;

  rotateX.value =
    (0.5 - relativeY) * 0.8;
};

const resetImagePosition = () => {
  translateX.value = 0;
  translateY.value = 0;
  rotateX.value = 0;
  rotateY.value = 0;
};

onMounted(() => {
  themeObserver = new MutationObserver(() => {
    theme.value =
      document.documentElement.getAttribute(
        "data-theme"
      ) || "light";
  });

  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });

  visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) {
        return;
      }

      isVisible.value = true;
      visibilityObserver?.disconnect();
    },
    {
      threshold: 0.16,
    }
  );

  if (sectionRef.value) {
    visibilityObserver.observe(sectionRef.value);
  }
});

onUnmounted(() => {
  themeObserver?.disconnect();
  visibilityObserver?.disconnect();
});
</script>

<style scoped>
.how-we-work {
  width: 1200px;
  max-width: calc(100vw - 48px);

  margin: 0 auto;

  position: relative;

  display: flex;
  flex-direction: column;

  padding-top: 80px;
  padding-bottom: 80px;

  box-sizing: border-box;

  overflow: hidden;
}

.content {
  position: relative;

  max-width: 600px;

  margin-bottom: 48px;
  padding-left: 22px;

  opacity: 0;
  transform: translateY(22px);

  transition:
    opacity 0.8s ease,
    transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

.how-we-work--visible .content {
  opacity: 1;
  transform: translateY(0);
}

.content-line {
  position: absolute;

  top: 6px;
  bottom: 3px;
  left: 0;

  width: 3px;

  border-radius: 999px;

  background:
    linear-gradient(
      180deg,
      #2277e6 0%,
      rgba(34, 119, 230, 0.08) 100%
    );

  transform: scaleY(0);
  transform-origin: top;

  transition:
    transform 0.8s 0.22s
      cubic-bezier(0.22, 1, 0.36, 1);
}

.how-we-work--visible .content-line {
  transform: scaleY(1);
}

.title {
  margin: 0 0 18px;

  display: inline-flex;
  align-items: baseline;
  gap: 4px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 32px;
  line-height: 1.05;
  letter-spacing: -1.2px;

  color: var(--text);
}

.question {
  display: inline-block;

  color: #2277e6;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 1.18em;
  line-height: 1;

  animation:
    question-float 3.5s ease-in-out infinite;
}

.description {
  margin: 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.65;
  letter-spacing: -0.3px;

  color:
    color-mix(
      in srgb,
      var(--text) 58%,
      transparent
    );
}

.description--secondary {
  margin-top: 14px;
}

.image-wrapper {
  width: 100%;

  opacity: 0;
  transform: translateY(28px);

  transition:
    opacity 0.95s 0.12s ease,
    transform 0.95s 0.12s
      cubic-bezier(0.22, 1, 0.36, 1);
}

.how-we-work--visible .image-wrapper {
  opacity: 1;
  transform: translateY(0);
}

.image {
  width: 100%;
  max-height: 680px;

  display: block;

  object-fit: contain;
  object-position: center;

  border-radius: 24px;

  animation:
    image-breathing 7s ease-in-out infinite;

  transition:
    transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.3s ease,
    opacity 0.3s ease;

  will-change: transform;
}

.image-wrapper:hover .image {
  filter:
    saturate(1.03)
    contrast(1.015);
}

@keyframes image-breathing {
  0%,
  100% {
    scale: 1;
  }

  50% {
    scale: 1.006;
  }
}

@keyframes question-float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-2px);
  }
}

@media (max-width: 768px) {
  .how-we-work {
    width: 100%;
    max-width: 100%;

    padding: 40px 16px 32px;

    gap: 24px;
  }

  .content {
    width: 100%;
    max-width: 100%;

    margin: 0;
    padding-left: 18px;

    box-sizing: border-box;

    text-align: left;
  }

  .title {
    margin-bottom: 14px;

    font-size: 24px;
    line-height: 1.08;
    letter-spacing: -0.8px;
  }

  .description {
    width: 100%;

    font-size: 14px;
    line-height: 1.55;

    text-align: left;
  }

  .description--secondary {
    margin-top: 12px;
  }

  .image-wrapper {
    width: 100%;

    transform: translateY(20px);
  }

  .how-we-work--visible .image-wrapper {
    transform: translateY(0);
  }

  .image {
    width: 100%;
    max-height: none;

    margin: 0 auto;

    border-radius: 16px;

    transform: none !important;

    animation-duration: 8s;
  }
}

@media (max-width: 430px) {
  .how-we-work {
    padding-top: 32px;
  }

  .image {
    border-radius: 14px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .content,
  .content-line,
  .image-wrapper,
  .image,
  .question {
    animation: none !important;
    transition-duration: 0.01ms !important;
  }

  .image {
    transform: none !important;
  }
}
</style>